/**
 * Storage de cadastros de clientes (admin).
 * Populado quando clientes se cadastram no app cliente (sync ou manual).
 */

import type { ClientePerfil } from '../../../shared/lib/clientePerfilTypes';

export type ClienteRegistro = ClientePerfil;

const KEY = 'naviex-clientes-registro';

function load(): ClienteRegistro[] {
  try {
    const s = localStorage.getItem(KEY);
    const raw = s ? (JSON.parse(s) as ClienteRegistro[]) : [];
    return raw.map((c) => ({
      ...c,
      sobrenome: c.sobrenome ?? '',
    }));
  } catch {
    return [];
  }
}

function save(list: ClienteRegistro[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function getClientes(): ClienteRegistro[] {
  return load().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function addCliente(data: Partial<Omit<ClienteRegistro, 'id' | 'createdAt' | 'updatedAt'>> & { email: string }): ClienteRegistro {
  const list = load();
  const now = new Date().toISOString();
  const emailNorm = (data.email || '').trim().toLowerCase();
  const existing = list.find((c) => (c.email || '').toLowerCase() === emailNorm);
  if (existing) {
    const merged: ClienteRegistro = {
      ...existing,
      ...data,
      nome: data.nome ?? existing.nome,
      sobrenome: data.sobrenome ?? existing.sobrenome ?? '',
      email: data.email,
      updatedAt: now,
    };
    const idx = list.findIndex((c) => c.id === existing.id);
    if (idx >= 0) list[idx] = merged;
    save(list);
    return merged;
  }
  const id = `cli-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const novo: ClienteRegistro = {
    id,
    nome: data.nome ?? '',
    sobrenome: data.sobrenome ?? '',
    apelido: data.apelido,
    telefonePais: data.telefonePais,
    telefone: data.telefone,
    tipoDocumento: data.tipoDocumento,
    numeroDocumento: data.numeroDocumento,
    email: data.email,
    paisOrigem: data.paisOrigem,
    estado: data.estado,
    cidade: data.cidade,
    cep: data.cep,
    endereco: data.endereco,
    sexo: data.sexo,
    dataNascimento: data.dataNascimento,
    chavePix: data.chavePix,
    provider: data.provider ?? 'email',
    createdAt: now,
    updatedAt: now,
  };
  list.unshift(novo);
  save(list);
  return novo;
}

export function updateCliente(id: string, data: Partial<Omit<ClienteRegistro, 'id' | 'createdAt'>>): ClienteRegistro | null {
  const list = load();
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  const updated: ClienteRegistro = {
    ...list[idx],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  list[idx] = updated;
  save(list);
  return updated;
}

export function removeCliente(id: string): void {
  save(load().filter((c) => c.id !== id));
}

export function findClienteByEmail(email: string): ClienteRegistro | undefined {
  const target = (email || '').trim().toLowerCase();
  if (!target) return undefined;
  return load().find((c) => (c.email || '').trim().toLowerCase() === target);
}
